/**
 * Toast.ts
 *
 * Module for the Toast UI
 */

import { IDs } from "./../UiConstants";
import * as Events from "./Events";
import * as Layout from "./Layout";
import * as Renderer from "./../Renderer";
import * as Utils from "./../Utils";
import * as Configuration from "./../../Configuration/Configuration";
import * as DynamicUxClient from "../DynamicUx/DynamicUxClient";
import * as Logging from "../../Logging/Logging";
import { DynamicUxClientState } from "@ms-ofb/floodgate-shared-typings";
import { CentroCoreDataSource } from "../DynamicUx/CentroCoreDataSource";

/**
 * UI initialization for toast
 */
export function createSurvey(onClose: () => void) {
	const dxEnabled = Configuration.get().getDynamicUxConfig().enabled;
	Logging.getLogger().logEvent(Logging.EventIds.SURVEY_UI_CREATE,
		Logging.LogLevel.Critical,
		{
			CampaignId: Configuration.get().getFloodgateSurvey()?.getCampaignId(),
			SurveyId: Configuration.get().getFloodgateSurvey()?.getId(),
			SurveyType: Configuration.get().getFloodgateSurvey()?.getSurveyType(),
			Message: JSON.stringify({
				DynamicUxClientState: dxEnabled && DynamicUxClient.get()?.getStatus(),
				DynamicUxEnabled: dxEnabled,
				SurveyEnabled: Configuration.get().checkIfSurveysEnabled()
			}),
		}
	);

	if (Configuration.get().getFloodgateSurvey().getUxSchema() &&
		dxEnabled && DynamicUxClient.get().getStatus() === DynamicUxClientState.Success) {
		CentroCoreDataSource.get().updateFloodgateSurvey(() => {
			DynamicUxClient.get().unmountSurvey();
			onClose();
		});
		DynamicUxClient.get().boostStrapSurvey().catch((error) => {
			Logging.getLogger().logEvent(Logging.EventIds.DYNAMICUX_BOOSTSTRAPPER_FAILURE,
				Logging.LogLevel.Error,
				{
					ErrorMessage: error,
					Message: JSON.stringify({
						DynamicUxClientState: DynamicUxClient.get().getStatus(),
					}),
				}
			);
			// Call onError to let host app know.
			Configuration.get().getCommonInitOptions().onError(`Failed to bootstrap Dynamic survey UX: ${error}`);
		});
	}
	else {
		create(Renderer.elementFromJson(Layout.generate()));
		Events.register(
			function (submitted: boolean) {
				Utils.setElementVisibility(IDs.ToastContainer, false);
				Utils.deleteElementById(IDs.ToastContainer);
				Configuration.get().getFloodgateInitOptions().onDismiss(
					Configuration.get().getFloodgateSurvey().getCampaignId(),
					submitted
				);
				onClose();
			});
	}
}

function create(userInterface: Element) {
	const surveyContainer = Configuration.get().getFloodgateInitOptions().surveyContainer;
	if (typeof surveyContainer === 'string') {
		// Element ID is provided
		const surveyContainerElement = Utils.getOrCreateDiv(surveyContainer);
		// Insert the survey UI as the first child of the survey container
		surveyContainerElement.insertBefore(userInterface, surveyContainerElement.firstChild);
	} else if (surveyContainer instanceof HTMLElement) { // TypeOf surveyContainer is "Object" when HtmlElement is passed, so checking for instanceOf
		// HtmlElement is provided
		surveyContainer.insertBefore(userInterface, surveyContainer.firstChild);
	} else {
		// Fallback to current drawing mechanism
		document.body.insertBefore(userInterface, document.body.firstChild);
	}

	Utils.setElementVisibility(IDs.ToastContainer, true);
}
